// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Policy document types for upload, versioning, and status tracking
 */

/**
 * Policy document status enum
 * Matches backend PolicyDocumentStatus enum
 */
export enum PolicyDocumentStatus {
  AWAITING_LOA = 'AWAITING_LOA',
  AWAITING_DOCUMENT = 'AWAITING_DOCUMENT',
  HAS_DOCUMENT = 'HAS_DOCUMENT',
  HAS_METADATA = 'HAS_METADATA',
  DOCUMENT_OUTDATED = 'DOCUMENT_OUTDATED'
}

/**
 * Result of document upload operation
 */
export interface PolicyDocumentUploadResult {
  policyId: string;
  version: number;
  filename: string;
  contentType: string;
  sizeBytes: number;
  uploadedAt: string;
  uploadedBy: string;
  changeSummary?: string;
  storagePath: string;
}

/**
 * Information about a specific document version
 */
export interface PolicyDocumentVersionInfo {
  version: number;
  filename: string;
  contentType: string;
  sizeBytes: number;
  uploadedAt: string;
  uploadedBy: string;
  changeSummary?: string;
  isLatest: boolean;
  uploadedBySupport?: boolean;
}

/**
 * Download result with blob data
 */
export interface PolicyDocumentDownloadResult {
  content: Blob;
  filename: string;
  contentType: string;
  version: number;
}

/**
 * Response for document existence check
 */
export interface PolicyDocumentExistsResponse {
  policyId: string;
  hasDocuments: boolean;
  latestVersion: number | null;
  versionCount: number;
  documentStatus: PolicyDocumentStatus;
}

/**
 * Error response from policy document API
 */
export interface PolicyDocumentError {
  code: string;
  message: string;
  field?: string;
}

/**
 * Upload progress state
 */
export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

/**
 * Allowed MIME types for policy documents
 */
export const ALLOWED_DOCUMENT_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain'
] as const;

/**
 * Maximum file size in bytes (50MB)
 */
export const MAX_FILE_SIZE = 50 * 1024 * 1024;

/**
 * Helper to format file size
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Number.parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Get icon name based on content type
 */
export function getFileIcon(contentType: string): string {
  if (contentType === 'application/pdf') return 'file pdf';
  if (contentType === 'image/png') return 'file png';
  if (contentType === 'image/jpeg' || contentType === 'image/jpg') return 'file jpg';
  if (contentType.startsWith('image/')) return 'file image outline';
  if (contentType.includes('excel') || contentType.includes('spreadsheet')) return 'file excel outline';
  if (contentType.includes('word')) return 'file word outline';
  if (contentType === 'text/csv') return 'file csv';
  if (contentType === 'text/plain') return 'file text outline';
  return 'file outline';
}
